import Div from '@ui/layout/Div'
import { useEffect } from 'react'
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from 'react-redux'
import { get } from '@util/xhrAPI'
import { addViewedSterilizationCall } from '@store/slice/sterilizationSlice'
import { useTranslation } from 'react-i18next'
import { ROLE_MAIRIE } from '@router/Routes'
import Buttons from '@ui/elements/Buttons'
import SplideJs from '@ui/component/SplideJs'
import Image from '@ui/elements/Image'
import useMainData from '@hook/useMainData'

const SterilizationCallShow = () => {
  const { id } = useParams()
  const { viewedCalls, selectedSeason } = useSelector(state => state.sterilization)
  const { mission, data } = useSelector(state => state.main)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [t] = useTranslation(['sterilization', 'mission'])
  const loadData = useMainData()
  const call = viewedCalls[id]

  const renderCallPhotos = () => {
    if (call.photos.length) {
      const photosSlides = call.photos.map(photo => {
        return (
          <Div cssClass='call-photo' key={`call-${call.id}-${photo.id}`}>
            <Image src={photo.scanPhoto} key={`scan-photo-call-${call.id}`} otherProps={{ 'data-splide-lazy': photo.scanPhoto }} />
            <Div cssClass='photoComment'>
              {photo.comment && <p><span>{t('sterilization:labels.comment')} : </span>{photo.comment}</p>}
              <p><span>{t('sterilization:labels.treated')} : </span>{photo.isTreated ? t('labels.yes') : t('labels.no')}</p>
              <p><span>{t('sterilization:labels.mission_subject')} : </span>{photo.missionSubjectName}</p>
              <p><span>{t('sterilization:labels.scan')} : </span>
                {photo.isFirstScan ? t('labels.first_scan') : (photo.isSecondScan ? t('labels.second_scan') : t('labels.non_indicated'))}</p>
            </Div>
          </Div>
        )
      })
      return <SplideJs slides={photosSlides} />
    }
    return <></>
  }

  const renderNuisances = () => {
    const { nuisances } = data
    const callNuisances = call.nuisances?.split(',') || []

    if (!nuisances || !nuisances.length || !callNuisances.length) return <></>

    const renderedNuisances = nuisances.map(nuisance => {
      if (callNuisances.indexOf(String(nuisance.value)) !== -1) {

        return (
          <h6 key={`${call.id}-${nuisance.label}-nuisance`}>
            <span>- {nuisance.label}</span>
          </h6>
        )
      }
    })

    return <>
      <Div cssClass='box-content'>
        <h2>{t('sterilization:titles.selected_nuisances')}</h2>
        {renderedNuisances}
      </Div>
    </>
  }

  const renderBuildings = () => {
    return call.buildings.map(building => {
      return (
        <Div key={building.key}>
          <h5>- <span>{building.name}</span>: {building.area} m²</h5>
        </Div>
      )
    })
  }

  const renderMissionSubjectsStat = missionSubjectsStat => {
    if (mission.missionSubjects.length > 1) {
      return mission.missionSubjects.map(missionSubject => {
        return (
          <h5 key={`${call.id}-${missionSubject.label}-stat`}>
            <span>{missionSubject.label}</span>: {missionSubjectsStat[missionSubject.label] || 0}
          </h5>
        )
      })
    }
  }

  const fetchInitialData = () => {
    const dataObject = {
      nuisances: 'nuisances'
    }
    loadData(dataObject)
  }

  useEffect(() => {
    if (id && 'undefined' !== typeof viewedCalls[id]) {
      fetchInitialData()
    }
  }, [viewedCalls])

  useEffect(() => {
    if (id && 'undefined' === typeof viewedCalls[id]) {
      get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-season/${selectedSeason || mission.activeSeason.id}/calls/${id}`,
        response => {
          const { code, sterilizationCall } = response
          if (200 === code) {
            dispatch(addViewedSterilizationCall(sterilizationCall))
          }
        })
    }
  }, [id])

  return (
    <>
      {call
        ?
        <Div>
          <Buttons buttons={{
            icon: 'icon-keyboard_arrow_left',
            cssClass: 'is-info is-small',
            callback: () => {
              navigate(ROLE_MAIRIE['sterilizations'])
            },
            text: t('sterilization:actions.back_to_sterilizations_list')
          }}
          />
          <h2 className='scene-title'>{t('sterilization:titles.call_infos')}</h2>
          <Div cssClass='sterilization-call columns is-multiline'>
            <Div cssClass='call-box call-responsible column is-5'>
              <Div cssClass='box-content'>
                <h2>{t('mission:titles.call_responsible')}</h2>
                <h3><span>{t('sterilization:titles.addressResponsibleCellPhone')}</span>: {call.addressResponsibleCellPhone}</h3>
                <h3><span>{t('sterilization:titles.addressResponsibleEmail')}</span>: {call.addressResponsibleEmail}</h3>
                <h3><span>{t('sterilization:titles.addressResponsibleFullName')}</span>: {call.addressResponsibleFullName}</h3>
                <h3><span>{t('mission:titles.isResponsibilityAssumed')}</span>: {call.isResponsibilityAssumed ? t('labels.yes') : t('labels.no')}</h3>
                <h3><span>{t('mission:titles.isTreatmentNotificationActive')}</span>: {call.isTreatmentNotificationActive ? t('labels.yes') : t('labels.no')}</h3>
                <h3><span>{t('mission:titles.createdAt')}</span>: {call.createdAt}</h3>
              </Div>

              {
                'company' === call.callType
                  ?
                  <Div cssClass='box-content'>
                    <h2>{t('mission:titles.call_company')}</h2>
                    <h3><span>{t('mission:titles.companyHomeAddress')}</span>: {call.companyHomeAddress}</h3>
                    <h3><span>{t('mission:titles.companyHomeCity')}</span>: {call.companyHomeCity.name}</h3>
                    <h3><span>{t('mission:titles.companyHomeZipCode')}</span>: {call.companyHomeZipCode}</h3>
                    <h3><span>{t('mission:titles.companyName')}</span>: {call.companyName}</h3>
                    <h3><span>{t('mission:titles.companyResponsibleCellPhone')}</span>: {call.companyResponsibleCellPhone}</h3>
                    <h3><span>{t('mission:titles.companyResponsibleEmail')}</span>: {call.companyResponsibleEmail}</h3>
                    <h3><span>{t('mission:titles.companyResponsibleFullName')}</span>: {call.companyResponsibleFullName}</h3>
                    <h3><span>{t('mission:titles.isSevezo')}</span>: {call.isSevezo ? t('labels.yes') : t('labels.no')}</h3>
                    <h3><span>{t('mission:titles.isUniqueResponsible')}</span>: {call.isUniqueResponsible ? t('labels.yes') : t('labels.no')}</h3>
                    <h3><span>{t('mission:titles.tvaNumber')}</span>: {call.tvaNumber}</h3>
                  </Div>
                  : <></>
              }

              <Div cssClass='box-content'>
                <h2>{t('sterilization:titles.call_scan')}</h2>
                <h3><span>{t('sterilization:titles.isFirstScanned')}</span>: {call.isFirstScanned ? t('labels.yes') : t('labels.no')}</h3>
                {call.scansData.isFirstScanned
                  ? <Div cssClass='column'>
                    <h5>
                      <span>{t('sterilization:labels.start')}</span> :
                      {call.treatmentJournals['firstScan'] ? call.treatmentJournals['firstScan'].startedAt : t('common:labels.non_filled')}
                    </h5>
                    <h5>
                      <span>{t('sterilization:labels.end')}</span> :
                      {call.treatmentJournals['firstScan'] ? call.treatmentJournals['firstScan'].endedAt : t('common:labels.non_filled')}
                    </h5>
                    <h5><span>{t('sterilization:labels.found_nests')}</span> :{call.scansData.firstScan.totalUnits}</h5>
                    <h5><span>{t('sterilization:labels.empty_nests')}</span> :{call.scansData.firstScan.totalEmptyUnits}</h5>
                    <h5><span>{t('sterilization:labels.nb_eggs')}</span> :{call.scansData.firstScan.totalEggs}</h5>
                    <h5><span>{t('sterilization:labels.nb_non_treated_eggs')}</span> :{call.scansData.firstScan.totalNonTreatedEggs}</h5>
                    <h5><span>{t('sterilization:labels.nb_birds')}</span> :{call.scansData.firstScan.totalBirds}</h5>
                    {renderMissionSubjectsStat(call.scansData.firstScan.missionSubjects)}
                  </Div>
                  : t('common:table.pending')
                }
                <h3><span>{t('sterilization:titles.isSecondScanned')}</span>: {call.isSecondScanned ? t('labels.yes') : t('labels.no')}</h3>
                {call.scansData.isSecondScanned
                  ? <Div cssClass='column'>
                    <h5>
                      <span>{t('sterilization:labels.start')}</span> :
                      {call.treatmentJournals['secondScan'] ? call.treatmentJournals['secondScan'].startedAt : t('common:labels.non_filled')}
                    </h5>
                    <h5>
                      <span>{t('sterilization:labels.end')}</span> :
                      {call.treatmentJournals['secondScan'] ? call.treatmentJournals['secondScan'].endedAt : t('common:labels.non_filled')}
                    </h5>
                    <h5><span>{t('sterilization:labels.found_nests')}</span> :{call.scansData.secondScan.totalUnits}</h5>
                    <h5><span>{t('sterilization:labels.empty_nests')}</span> :{call.scansData.secondScan.totalEmptyUnits}</h5>
                    <h5><span>{t('sterilization:labels.nb_eggs')}</span> :{call.scansData.secondScan.totalEggs}</h5>
                    <h5><span>{t('sterilization:labels.nb_non_treated_eggs')}</span> :{call.scansData.secondScan.totalNonTreatedEggs}</h5>
                    <h5><span>{t('sterilization:labels.nb_birds')}</span> :{call.scansData.secondScan.totalBirds}</h5>
                    {renderMissionSubjectsStat(call.scansData.firstScan.missionSubjects)}
                  </Div>
                  : t('common:table.pending')
                }
              </Div>

              {renderNuisances()}
            </Div>

            <Div cssClass='call-box call-scan  column is-7'>
              <Div cssClass='box-content'>
                <h2>{t('mission:titles.call_address')}</h2>
                <h3><span>{t('mission:titles.sterilizationAddress')}</span>: {call.sterilizationAddress}</h3>
                <h3><span>{t('mission:titles.sterilizationCity')}</span>: {call.sterilizationCity.name}</h3>
                <h3><span>{t('mission:titles.sterilizationDistrict')}</span>: {call.sterilizationDistrict}</h3>
                <h3><span>{t('mission:titles.sterilizationZipCode')}</span>: {call.sterilizationZipCode}</h3>
                {call.unitsNumber && <h3><span>{t('sterilization:labels.signaled_nests')}</span>: {call.unitsNumber}</h3>}
                {
                  'company' === call.callType
                  && <Div>
                    <h3><span>{t('sterilization:labels.buildings')}</span> :</h3>
                    {renderBuildings()}
                  </Div>
                }
              </Div>

              <Div cssClass='call-box'>
                {renderCallPhotos()}
              </Div>
            </Div>
          </Div>
        </Div >
        : <></>
      }
    </>
  )
}

export default SterilizationCallShow